import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { apiBaseUrl } from '../config/runtime';
import './FindUserIdPage.css';

interface FoundAccount {
  email: string;
  registeredAt: string;
  loginMethod: string;
}

const FindUserIdPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [foundAccounts, setFoundAccounts] = useState<FoundAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFoundAccounts([]);

    if (!name || !phone) {
      setError(t('findId.errBoth'));
      return;
    }

    try {
      setLoading(true);
      console.log('🔍 아이디 찾기 요청:', { name, phone });

      const response = await axios.post(apiBaseUrl('/auth/find/userid'), {
        name: name.trim(),
        phone: phone.trim()
      });

      console.log('✅ 아이디 찾기 성공:', response.data);

      setFoundAccounts(response.data.foundAccounts || []);

    } catch (err: any) {
      console.error('❌ 아이디 찾기 실패:', err);
      setError(
        err.response?.data?.message || 
        t('findId.notFound')
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getLoginMethodText = (method: string) => {
    switch (method) {
      case 'email':
        return t('profile.email');
      case 'google':
        return t('profile.google');
      case 'kakao':
        return t('profile.kakao');
      default:
        return method;
    }
  };

  return (
    <div className="find-userid-container">
      <div className="find-userid-box">
        <h1>🔍 {t('login.findId')}</h1>
        <p className="subtitle">{t('findId.subtitle')}</p>

        {foundAccounts.length === 0 ? (
          <form onSubmit={handleSubmit} className="find-form">
            <div className="form-group">
              <label htmlFor="name">{t('profile.namePlaceholder')}</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('findId.namePlaceholder')}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">{t('findId.phone')}</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="010-1234-5678"
                disabled={loading}
              />
              <small className="hint">{t('findId.phoneHint')}</small>
            </div>

            {error && (
              <div className="error-message">
                <span>⚠️ {error}</span>
              </div>
            )}

            <button 
              type="submit" 
              className="btn-submit"
              disabled={loading}
            >
              {loading ? t('findId.searching') : t('login.findId')}
            </button>

            <div className="links">
              <Link to="/login">{t('findId.backToLogin')}</Link>
              <span className="separator">|</span>
              <Link to="/forgot-password">{t('login.findPassword')}</Link>
            </div>
          </form>
        ) : (
          <div className="result-section">
            <div className="success-icon">✅</div>
            <h2>{t('findId.foundN', { n: foundAccounts.length })}</h2>

            <div className="accounts-list">
              {foundAccounts.map((account, index) => (
                <div key={index} className="account-card">
                  <div className="account-header">
                    <span className="account-number">{t('findId.accountN', { n: index + 1 })}</span>
                    <span className="login-method">
                      {t('findId.loginVia', { m: getLoginMethodText(account.loginMethod) })}
                    </span>
                  </div>
                  <div className="account-email">
                    <strong>{t('profile.email')}:</strong> {account.email}
                  </div>
                  <div className="account-date">
                    <strong>{t('profile.joined')}</strong> {formatDate(account.registeredAt)}
                  </div>
                </div>
              ))}
            </div>

            <div className="action-buttons">
              <button
                onClick={() => navigate('/login')}
                className="btn-goto-login"
              >
                {t('findId.goLogin')}
              </button>
              <button 
                onClick={() => navigate('/forgot-password')}
                className="btn-password"
              >
                {t('login.findPassword')}
              </button>
              <button 
                onClick={() => {
                  setFoundAccounts([]);
                  setName('');
                  setPhone('');
                  setError('');
                }}
                className="btn-search-again"
              >
                {t('findId.searchAgain')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindUserIdPage;
